import useUpdateDocumentField from 'hooks/DocumentsRepository/useUpdateDocumentField';
import useUpdateUserDocumentField from 'hooks/DocumentsRepository/useUpdateUserDocumentField';
import type {
  DocumentReferenceField,
  DocumentReferenceFieldUser,
} from 'types/repository.types';
import DocumentRepositoryFieldItem from './DocumentRepositoryFieldItem';
import { useTranslation } from 'react-i18next';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import type { IUser } from 'types/users.types';

import type { JSX } from 'react';

type DocumentRepositoryUserFieldProps = {
  field: DocumentReferenceField & DocumentReferenceFieldUser;
  documentId: string;
  setBoundingBoxField: (field: string | null) => void;
};

const fieldUserMap: Record<DocumentReferenceFieldUser['type'], keyof IUser> = {
  VATNumber: 'VATNumber',
  VATRegime: 'VATType',
  VATFrequency: 'VATReturnFrequency',
  steuernummer: 'steuernummer',
} as const;

const DocumentRepositoryUserField = ({
  field,
  documentId,
}: DocumentRepositoryUserFieldProps): JSX.Element => {
  const { t } = useTranslation();

  const {
    mutate: updateUserDocumentField,
    isLoading: isUpdateUserDocumentLoading,
  } = useUpdateUserDocumentField();

  const {
    mutate: updateDocumentField,
    isLoading: isUpdateDocumentFieldLoading,
  } = useUpdateDocumentField(documentId);

  const isLoading = isUpdateUserDocumentLoading || isUpdateDocumentFieldLoading;

  const handleAccept = () => {
    const fieldKey = fieldUserMap[field.type];
    if (!fieldKey || !field.value) return;

    updateUserDocumentField({ [fieldKey]: field.value });
    updateDocumentField({ ...field, status: 'accepted' });
  };

  const handleReject = () =>
    updateDocumentField({ ...field, status: 'rejected' });

  const user = useGetCurrentCustomerData().data as IUser;

  return (
    <DocumentRepositoryFieldItem
      field={field}
      onAccept={handleAccept}
      onReject={handleReject}
      isLoading={isLoading}
      title={t(`repository.document_form.fields.${field.type}.title`, {
        oldVATType: user.VATType,
        value: field.value,
      })}
      description={t(
        `repository.document_form.fields.${field.type}.description`,
        {
          oldVATType: user.VATType,
          value: field.value,
        },
      )}
      acceptButtonLabel={t('repository.document_form.fields.accept')}
      rejectButtonLabel={t('repository.document_form.fields.reject')}
    />
  );
};

export default DocumentRepositoryUserField;
